/**
 * SectionJumpBar — sticky chip strip for the details tab. One
 * IntersectionObserver over the section ids marks the active chip; click
 * scrolls the section into view (smooth unless reduced-motion).
 */

import { useEffect, useState } from "react";

const sectionDomId = (id) => `ncd2-sec-${id}`;

export default function SectionJumpBar({ sections }) {
  const [active, setActive] = useState(sections[0]?.id || null);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;
    const els = sections.map((s) => document.getElementById(sectionDomId(s.id))).filter(Boolean);
    if (els.length === 0) return undefined;
    const visible = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) visible.set(e.target.id, e.intersectionRatio);
          else visible.delete(e.target.id);
        });
        if (visible.size === 0) return;
        // Topmost visible section wins (document order)
        const first = els.find((el) => visible.has(el.id));
        if (first) setActive(first.id.replace("ncd2-sec-", ""));
      },
      { rootMargin: "-20% 0px -60% 0px", threshold: [0, 0.1, 0.5] }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [sections]);

  const jump = (id) => {
    const el = document.getElementById(sectionDomId(id));
    if (!el) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
    setActive(id);
  };

  return (
    <nav className="ncd2-jump" aria-label="ניווט בין חלקי העמוד">
      {sections.map((s) => (
        <button
          key={s.id}
          type="button"
          className={`ncd2-jump-chip${active === s.id ? " is-active" : ""}`}
          onClick={() => jump(s.id)}
          aria-current={active === s.id ? "true" : undefined}
        >
          {s.label}
          {s.count > 0 && <span className="ncd2-jump-count">{s.count}</span>}
        </button>
      ))}
    </nav>
  );
}

export { sectionDomId };
